import {
  Archive,
  Clock,
  Download,
  FolderInput,
  FolderLock,
  Forward,
  Mail,
  MailOpen,
  Paperclip,
  RefreshCw,
  Reply,
  ReplyAll,
  Star,
  Flag,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import type { MailContextMenuItem } from "@/features/shared/MailContextMenu";
import { MAIL_COLOR_LABELS, type MailColorLabel } from "@/lib/mail/messageLabels";

export type MailActionId =
  | "reply"
  | "replyAll"
  | "forward"
  | "delete"
  | "markRead"
  | "markUnread"
  | "move"
  | "toggleImportant"
  | "toggleFavorite"
  | `color:${MailColorLabel}`
  | "color:none"
  | "snooze:pick"
  | "snooze:cancel"
  | "selectWithAttachments"
  | "downloadAttachments"
  | "saveToCoffre"
  | "archiveToCoffre"
  | "redownloadEml";

type BuildMenuOptions = {
  compose?: boolean;
  seen?: boolean;
  flagged?: boolean;
  favorite?: boolean;
  colorLabel?: MailColorLabel | null;
  bulk?: boolean;
  bulkCount?: number;
  snoozed?: boolean;
  showSnooze?: boolean;
  showMove?: boolean;
  showLabels?: boolean;
  showAttachmentActions?: boolean;
  attachmentMessageCount?: number;
  showArchiveToCoffre?: boolean;
  showRedownloadEml?: boolean;
};

function snoozeItems(options: { snoozed?: boolean; bulk?: boolean }): MailContextMenuItem[] {
  if (options.bulk) {
    return [
      {
        id: "snooze:pick",
        label: "Mettre en attente…",
        icon: Clock,
        disabled: true,
        dividerAfter: true,
      },
    ];
  }
  if (options.snoozed) {
    return [
      {
        id: "snooze:cancel",
        label: "Annuler le report",
        icon: Clock,
        dividerAfter: true,
      },
    ];
  }
  return [
    {
      id: "snooze:pick",
      label: "Mettre en attente…",
      icon: Clock,
      dividerAfter: true,
    },
  ];
}

function composeItems(disabled = false): MailContextMenuItem[] {
  return [
    { id: "reply", label: "Répondre", icon: Reply, disabled },
    { id: "replyAll", label: "Répondre à tous", icon: ReplyAll, disabled },
    { id: "forward", label: "Transférer", icon: Forward, disabled, dividerAfter: true },
  ];
}

function labelItems(options: {
  flagged?: boolean;
  favorite?: boolean;
  colorLabel?: MailColorLabel | null;
  bulk?: boolean;
}): MailContextMenuItem[] {
  const { flagged = false, favorite = false, colorLabel = null, bulk = false } = options;
  const items: MailContextMenuItem[] = [
    {
      id: "toggleImportant",
      label: flagged
        ? bulk
          ? "Retirer important"
          : "Retirer important"
        : bulk
          ? "Marquer important"
          : "Important",
      icon: Flag,
    },
    {
      id: "toggleFavorite",
      label: favorite
        ? bulk
          ? "Retirer des favoris"
          : "Retirer des favoris"
        : bulk
          ? "Ajouter aux favoris"
          : "Favoris",
      icon: Star,
      dividerAfter: true,
    },
    { id: "color-group", label: "Couleur", kind: "color-header" },
    ...MAIL_COLOR_LABELS.map((color) => ({
      id: `color:${color.id}` as const,
      label: color.label,
      kind: "color" as const,
      colorSwatch: color.swatch,
      active: colorLabel === color.id,
    })),
    {
      id: "color:none",
      label: "Aucune couleur",
      kind: "color-clear" as const,
      active: colorLabel == null,
      dividerAfter: true,
    },
  ];

  return items;
}

function attachmentItems(options: {
  bulk?: boolean;
  attachmentMessageCount?: number;
}): MailContextMenuItem[] {
  const count = options.attachmentMessageCount ?? 0;
  const suffix = count > 0 ? ` (${count})` : "";
  return [
    {
      id: "selectWithAttachments",
      label: options.bulk ? "Sélectionner les messages avec PJ" : "Sélectionner tous les messages avec PJ",
      icon: Paperclip,
    },
    {
      id: "downloadAttachments",
      label: `Télécharger les PJ${suffix}`,
      icon: Download,
    },
    {
      id: "saveToCoffre",
      label: `Enregistrer dans Coffre${suffix}`,
      icon: FolderLock,
      dividerAfter: true,
    },
  ];
}

export function buildMessageContextMenuItems(options: BuildMenuOptions = {}): MailContextMenuItem[] {
  const {
    compose = true,
    seen,
    flagged = false,
    favorite = false,
    colorLabel = null,
    bulk = false,
    bulkCount = 1,
    snoozed = false,
    showSnooze = true,
    showMove = true,
    showLabels = true,
    showAttachmentActions = false,
    attachmentMessageCount = 0,
    showArchiveToCoffre = true,
    showRedownloadEml = false,
  } = options;
  const items: MailContextMenuItem[] = [];

  if (compose) {
    items.push(...composeItems(bulk));
  }

  if (bulk) {
    items.push(
      { id: "markRead", label: "Marquer comme lu", icon: MailOpen },
      { id: "markUnread", label: "Marquer comme non lu", icon: Mail, dividerAfter: true }
    );
  } else {
    items.push(
      seen
        ? { id: "markUnread", label: "Marquer comme non lu", icon: Mail, dividerAfter: !showMove }
        : { id: "markRead", label: "Marquer comme lu", icon: MailOpen, dividerAfter: !showMove }
    );
    if (showMove) {
      items.push({ id: "move", label: "Déplacer vers…", icon: FolderInput, dividerAfter: true });
    }
  }

  if (showArchiveToCoffre) {
    items.push({
      id: "archiveToCoffre",
      label: bulk ? `Archiver dans Coffre (${bulkCount})` : "Archiver dans Coffre",
      icon: Archive,
      dividerAfter: true,
    });
  }

  if (showSnooze) {
    items.push(...snoozeItems({ snoozed, bulk }));
  }

  if (showLabels) {
    items.push(...labelItems({ flagged, favorite, colorLabel, bulk }));
  }

  if (showAttachmentActions) {
    items.push(...attachmentItems({ bulk, attachmentMessageCount }));
  }

  if (showRedownloadEml && !bulk) {
    items.push({
      id: "redownloadEml",
      label: "Retélécharger depuis IMAP",
      icon: RefreshCw,
      dividerAfter: true,
    });
  }

  items.push({
    id: "delete",
    label: bulk ? `Supprimer (${bulkCount})` : "Supprimer",
    icon: Trash2,
    danger: true,
  });

  if (bulk && showMove) {
    items.splice(items.length - 1, 0, {
      id: "move",
      label: `Déplacer (${bulkCount})…`,
      icon: FolderInput,
      dividerAfter: true,
    });
  }

  return items;
}

export type ReaderToolbarAction = {
  id: MailActionId;
  label: string;
  icon: LucideIcon;
  danger?: boolean;
};

export function buildReaderToolbarActions(seen: boolean): ReaderToolbarAction[] {
  return [
    { id: "reply", label: "Répondre", icon: Reply },
    { id: "replyAll", label: "Répondre à tous", icon: ReplyAll },
    { id: "forward", label: "Transférer", icon: Forward },
    seen
      ? { id: "markUnread", label: "Marquer comme non lu", icon: Mail }
      : { id: "markRead", label: "Marquer comme lu", icon: MailOpen },
    { id: "delete", label: "Supprimer", icon: Trash2, danger: true },
  ];
}
